import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  AIMessage,
  ChatSession,
  TelemetryContext,
  ToolHandler,
} from "./types";
import { sendChatMessage } from "./aiService";
import { TOOL_DISPLAY_NAMES } from "./tools";

type GetAccessToken = () => Promise<string>;

interface AIChatState {
  isOpen: boolean;
  isLoading: boolean;
  isSidebarOpen: boolean;
  statusHistory: string[];
  error: string | null;

  sessions: ChatSession[];
  activeSessionId: string | null;
  messages: AIMessage[];

  context: TelemetryContext;

  setOpen: (open: boolean) => void;
  toggleOpen: () => void;
  toggleSidebar: () => void;
  setContext: (ctx: TelemetryContext) => void;
  clearError: () => void;

  createSession: (title?: string) => string;
  switchSession: (id: string) => void;
  deleteSession: (id: string) => void;
  renameSession: (id: string, title: string) => void;
  clearActiveSession: () => void;

  sendMessage: (
    message: string,
    toolHandler: ToolHandler,
    getAccessTokenSilently: GetAccessToken,
  ) => Promise<void>;
}

const newId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? (crypto as unknown as { randomUUID: () => string }).randomUUID()
    : `id_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const emptySession = (title = "New Chat"): ChatSession => ({
  id: newId(),
  title,
  messages: [],
  createdAt: new Date(),
  updatedAt: new Date(),
});

const titleFromMessages = (messages: AIMessage[]): string => {
  const firstUser = messages.find((m) => m.role === "user");
  if (!firstUser) return "New Chat";
  const text = firstUser.content.slice(0, 40);
  return text.length < firstUser.content.length
    ? text.replace(/\s+\S*$/, "...")
    : text || "New Chat";
};

export const useKateChatStore = create<AIChatState>()(
  persist(
    (set, get) => ({
      isOpen: false,
      isLoading: false,
      isSidebarOpen: false,
      statusHistory: [],
      error: null,

      sessions: [],
      activeSessionId: null,
      messages: [],

      context: {},

      setOpen: (open) => set({ isOpen: open }),
      toggleOpen: () => set((s) => ({ isOpen: !s.isOpen })),
      toggleSidebar: () => set((s) => ({ isSidebarOpen: !s.isSidebarOpen })),
      setContext: (ctx) => set({ context: ctx }),
      clearError: () => set({ error: null }),

      createSession: (title) => {
        const session = emptySession(title);
        set((s) => ({
          sessions: [session, ...s.sessions],
          activeSessionId: session.id,
          messages: [],
        }));
        return session.id;
      },

      switchSession: (id) => {
        const target = get().sessions.find((s) => s.id === id);
        if (target) {
          set({ activeSessionId: id, messages: target.messages });
        }
      },

      deleteSession: (id) =>
        set((state) => {
          const remaining = state.sessions.filter((s) => s.id !== id);
          if (state.activeSessionId !== id) {
            return { sessions: remaining };
          }
          if (remaining.length > 0) {
            return {
              sessions: remaining,
              activeSessionId: remaining[0].id,
              messages: remaining[0].messages,
            };
          }
          const fresh = emptySession();
          return {
            sessions: [fresh],
            activeSessionId: fresh.id,
            messages: [],
          };
        }),

      renameSession: (id, title) =>
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === id ? { ...s, title, updatedAt: new Date() } : s,
          ),
        })),

      clearActiveSession: () =>
        set((state) => {
          if (!state.activeSessionId) return {};
          return {
            sessions: state.sessions.map((s) =>
              s.id === state.activeSessionId
                ? { ...s, messages: [], title: "New Chat", updatedAt: new Date() }
                : s,
            ),
            messages: [],
          };
        }),

      sendMessage: async (message, toolHandler, getAccessTokenSilently) => {
        let state = get();
        if (!state.activeSessionId) {
          get().createSession();
          state = get();
        }

        const userMessage: AIMessage = {
          id: newId(),
          role: "user",
          content: message,
          timestamp: new Date(),
        };

        set((s) => {
          const sessions = s.sessions.map((session) => {
            if (session.id !== s.activeSessionId) return session;
            const newMessages = [...session.messages, userMessage];
            return {
              ...session,
              messages: newMessages,
              title:
                session.title === "New Chat"
                  ? titleFromMessages(newMessages)
                  : session.title,
              updatedAt: new Date(),
            };
          });
          const active = sessions.find((sess) => sess.id === s.activeSessionId);
          return {
            sessions,
            messages: active?.messages ?? [],
            isLoading: true,
            error: null,
            statusHistory: ["Sending to Kate..."],
          };
        });

        try {
          const onToolStart = (toolName: string) => {
            const display = TOOL_DISPLAY_NAMES[toolName] || `Using ${toolName}`;
            set((s) => ({
              statusHistory: [...s.statusHistory, display],
            }));
          };

          const priorMessages = get().messages.filter(
            (m) => m.id !== userMessage.id,
          );

          const assistant = await sendChatMessage({
            userMessage: message,
            conversationHistory: priorMessages,
            context: get().context,
            toolHandler,
            getAccessTokenSilently,
            onToolStart,
          });

          set((s) => {
            const log = [...s.statusHistory];
            const sessions = s.sessions.map((session) => {
              if (session.id !== s.activeSessionId) return session;
              const enriched: AIMessage = {
                ...assistant,
                executionLog: log.length > 0 ? log : undefined,
              };
              return {
                ...session,
                messages: [...session.messages, enriched],
                updatedAt: new Date(),
              };
            });
            const active = sessions.find((sess) => sess.id === s.activeSessionId);
            return {
              sessions,
              messages: active?.messages ?? [],
              isLoading: false,
              statusHistory: [],
            };
          });
        } catch (err) {
          console.error("Kate sendMessage error", err);
          set({
            isLoading: false,
            statusHistory: [],
            error: err instanceof Error ? err.message : "Failed to get response",
          });
        }
      },
    }),
    {
      name: "telemetry-kate-chat",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        sessions: state.sessions,
        activeSessionId: state.activeSessionId,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // Dates round-trip to strings through JSON; coerce them back.
        for (const s of state.sessions) {
          s.createdAt = new Date(s.createdAt);
          s.updatedAt = new Date(s.updatedAt);
          for (const m of s.messages) {
            m.timestamp = new Date(m.timestamp);
          }
        }
        const active = state.sessions.find((s) => s.id === state.activeSessionId);
        state.messages = active?.messages ?? [];
      },
    },
  ),
);
